// Imports for external libraries go here.
import React, { FC } from 'react';
import clsx from 'clsx';
// Imports for internal (to the monorepo) libraries go here,
// separated by a blank line from external imports.
// The closer the import is to the file the lower it should be in this list.
import { LuxuryCarouselProps } from './CardCarouselContainer.types';
import { StyledCardCarouselContainer, StyledLuxuryCarousel } from './CardCarouselContainer.styles';
import { EditableComponent } from '@adobe/aem-react-editable-components';
import { HeaderTextCta } from '../../molecules/HeaderTextCta';
import { HeaderTextCtaModel } from '../../molecules/HeaderTextCta/HeaderTextCta.types';
import { LuxuryCarousel } from '../../molecules/LuxuryCarousel';
import { useDCADynamicModelAlloy } from '../../modules/helpers';
import { TARGET_CARD, MODEL_CARD } from '../../modules/constants/constant';

export const LuxuryCarouselCont: FC<LuxuryCarouselProps> = props => {
  /**
   * Fetching the target response
   */
  const mboxParam = props?.model?.mboxParameter;

  /***
   * Makes the call to target if mboxParam is authored
   */
  const targetResponse = useDCADynamicModelAlloy(mboxParam);
  const targetData = targetResponse?.targetData;

  /**
   * Replacing the AEM model data with target data if target Response is present
   */
  const data = targetData ? targetData : props?.model;

  const regex = new RegExp('color-scheme2|color-scheme4|color-scheme7');
  const headerModel: HeaderTextCtaModel = {
    bodyText: data?.bodyText,
    enableTracking: data?.enableTracking,
    logo: data?.logo,
    styleclass: data?.styleclass,
    eyebrowText: data?.eyebrowText,
    clickTrackValue: data?.clickTrackValue,
    headerPrimaryHOneTag: data?.headerPrimaryHOneTag,
    headerText: data?.headerText,
    headerFontSize: data?.headerFontSize,
    location: data?.location,
    alignment: data?.alignment,
    ctaProperties: {
      ctaType: data?.ctaProperties?.ctaType,
      openInANewTab: data?.ctaProperties?.openInANewTab,
      linkUrl: data?.ctaProperties?.linkUrl,
      linkText: data?.ctaProperties?.linkText,
      linkType: data?.ctaProperties?.linkType,
    },
  };
  const allowedComponents = [
    {
      path: '/content/mi-luxury-spa/language-masters/na/en-us/destinations/jcr:content/root/responsivegrid/wcm/foundation/components/responsivegrid',
      title: 'Layout Container',
    },
    {
      path: '/content/mi-luxury-spa/language-masters/na/en-us/destinations/jcr:content/root/responsivegrid/mi-aem-luxury-spa/components/content/cardcarouselcontainer/v1/cardcarouselcontainer',
      title: 'Card Carousel Container',
    },
    {
      path: '/content/mi-luxury-spa/language-masters/na/en-us/destinations/jcr:content/root/responsivegrid/mi-aem-luxury-spa/components/container',
      title: 'Container',
    },
    {
      path: '/content/mi-luxury-spa/language-masters/na/en-us/destinations/jcr:content/root/responsivegrid/mi-aem-luxury-spa/components/content/header-text-cta/v1/header-text-cta',
      title: 'Header with Text and CTA',
    },
    {
      path: '/content/mi-luxury-spa/language-masters/na/en-us/destinations/jcr:content/root/responsivegrid/mi-aem-common-spa/components/content/cardlayered',
      title: 'Card Layered',
    },
    {
      path: '/content/mi-luxury-spa/language-masters/na/en-us/destinations/jcr:content/root/responsivegrid/mi-aem-common-spa/components/content/cardvertical',
      title: 'Card Vertical',
    },
  ];

  return (
    <StyledLuxuryCarousel data-testid="luxury-LuxuryCarousel" data-component-name="o-luxury-LuxuryCarousel">
      <HeaderTextCta model={headerModel} />
      <div className={clsx(data?.styleclass && regex.test(data?.styleclass) && 'color-class')}>
        {targetData ? (
          <LuxuryCarousel
            cardId={TARGET_CARD}
            headerText={targetData?.headerText}
            subHeaderText={targetData?.bodyText}
            noOfCards={targetData?.noOfCards}
            ctaLabel={targetData?.ctaProperties?.linkText}
            ctaLink={targetData?.ctaProperties?.linkUrl}
            ctaType={targetData?.ctaProperties?.linkType}
            cardTypes={targetData?.cardTypes}
            cardCount={targetData?.cardCount}
            isRandom={targetData?.isRandom}
            openInaNewTab={false}
            totalNumberOfCards={targetData?.totalNumberOfCards}
            trackingProperties={targetData?.trackingProperties}
            styleclass={''}
            cqItems={targetData[':items'] ?? props?.model?.cqItems}
            componentId={targetData?.componentId}
            pagePath={targetData?.pagePath}
            itemPath={targetData?.itemPath}
            wcmMode={targetData?.wcmMode}
            isAuthorMode={props?.isAuthorMode ?? false}
            noOfCardsTablet={targetData?.noOfCardsTablet}
            allowedComponents={allowedComponents}
            previousLabel={targetData?.previousLabel}
            nextLabel={targetData?.nextLabel}
          />
        ) : (
          <LuxuryCarousel
            cardId={MODEL_CARD}
            headerText={props?.model?.headerText}
            subHeaderText={props?.model?.bodyText}
            noOfCards={props?.model?.noOfCards}
            ctaLabel={props?.model?.ctaProperties?.linkText}
            ctaLink={props?.model?.ctaProperties?.linkUrl}
            ctaType={props?.model?.ctaProperties?.linkType}
            cardTypes={props?.model?.cardTypes}
            cardCount={props?.model?.cardCount}
            isRandom={props?.model?.isRandom}
            openInaNewTab={false}
            totalNumberOfCards={props?.model?.totalNumberOfCards}
            trackingProperties={props?.model?.trackingProperties}
            styleclass={''}
            cqItems={props?.model[':items'] ?? props?.model?.cqItems}
            componentId={props?.model?.componentId}
            pagePath={props?.model?.pagePath}
            itemPath={props?.model?.itemPath}
            wcmMode={props?.model?.wcmMode}
            isAuthorMode={props?.isAuthorMode ?? false}
            noOfCardsTablet={props?.model?.noOfCardsTablet}
            allowedComponents={allowedComponents}
            previousLabel={props?.model?.previousLabel}
            nextLabel={props?.model?.nextLabel}
          />
        )}
      </div>
    </StyledLuxuryCarousel>
  );
};

export const LuxuryCarouselConfig = {
  emptyLabel: 'LuxuryCarouselCont',
  isEmpty: () => true,
  resourceType: `mi-aem-luxury-spa/components/content/cardcarouselcontainer/v1/cardcarouselcontainer`,
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const CardCarouselContainer = (props: any) => (
  <StyledCardCarouselContainer className={clsx(props?.model?.styleclass)}>
    <div className="container" data-testid="luxury-Carousel" data-component-name="m-luxury-Carousel">
      <EditableComponent config={LuxuryCarouselConfig} {...props}>
        <LuxuryCarouselCont {...props} />
      </EditableComponent>
    </div>
  </StyledCardCarouselContainer>
);
